import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';

import {
  getPlaygroundOptions,
  listPlaygroundRuns,
  rerunPlaygroundRun,
  type PlaygroundRun,
  type PlaygroundRunSummary,
} from '@api/demand-playground';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import RunForm from '../../components/RunForm';
import RunDetail, { runKey } from '../../components/RunDetail';
import RunsList from '../../components/RunsList';
import {
  POLL_MS,
  canUsePlayground,
  errorText,
  isOffError,
  isRunInProgress,
} from '../../utils/playground';

export const NOT_AVAILABLE = 'This page is not available.';
export const SAFETY_LINE =
  'Made-up test cases only. Nothing here changes the live demand letter.';

const OPTIONS_KEY = 'demand-playground-options';
const RUNS_KEY = 'demand-playground-runs';

const positiveId = (raw: string | null): number | null => {
  const value = Number(raw);
  return raw && Number.isInteger(value) && value > 0 ? value : null;
};

/**
 * Demand letter test playground, staff only (ZLAYT-1660). Try new demand
 * letter prompts on a made-up test case and read the letter next to the one
 * the current prompts write, each with its fact check, scores, time and
 * cost. Shown only to a superuser while the server says it is on; the server
 * refuses everyone else (404) as well. Nothing here changes the live setup.
 */
function DemandPlayground() {
  const isSuperuser = useAuthSelector(
    (state) =>
      (state.userData as { is_superuser?: boolean } | null)?.is_superuser ===
      true
  );
  const [params, setParams] = useSearchParams();
  const selectedId = positiveId(params.get('run'));
  const [rerunning, setRerunning] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const rerunRef = useRef(false);
  const { mutate } = useSWRConfig();

  const { data: options, error: optionsError } = useSWR(
    isSuperuser ? OPTIONS_KEY : null,
    getPlaygroundOptions,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: (err: unknown) => !isOffError(err),
    }
  );
  const allowed = canUsePlayground(
    isSuperuser ? { is_superuser: true } : null,
    options
  );

  const {
    data: runs,
    error: runsError,
    mutate: mutateRuns,
  } = useSWR<PlaygroundRunSummary[]>(
    allowed ? RUNS_KEY : null,
    listPlaygroundRuns,
    {
      refreshInterval: (latest) =>
        latest?.some((r) => isRunInProgress(r.status)) ? POLL_MS : 0,
    }
  );

  const open = (id: number) => {
    setRerunError(null);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('run', String(id));
        return next;
      },
      { replace: false }
    );
  };

  const started = async (run: PlaygroundRun) => {
    // Into the cache under the run's own key first, so it shows at once.
    await mutate(runKey(run.id), run, { revalidate: false });
    open(run.id);
    mutateRuns();
  };

  const rerun = (run: PlaygroundRun) => {
    if (rerunRef.current) return;
    rerunRef.current = true;
    setRerunning(true);
    setRerunError(null);
    rerunPlaygroundRun(run.id)
      .then(started)
      .catch((err) => setRerunError(errorText(err)))
      .finally(() => {
        rerunRef.current = false;
        setRerunning(false);
      });
  };

  if (!isSuperuser) {
    return <Typography sx={{ color: '#475467' }}>{NOT_AVAILABLE}</Typography>;
  }
  if (!options && !optionsError) {
    return <CircularProgress size={20} />;
  }
  if (optionsError && !isOffError(optionsError)) {
    return <Alert severity="error">{errorText(optionsError)}</Alert>;
  }
  if (!allowed || !options) {
    return <Typography sx={{ color: '#475467' }}>{NOT_AVAILABLE}</Typography>;
  }

  return (
    <Stack spacing={3} sx={{ py: 2 }}>
      <Alert severity="info">{SAFETY_LINE}</Alert>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <RunForm options={options} onStarted={started} />
        <RunsList
          runs={runs}
          error={runsError}
          models={options.models}
          selectedId={selectedId}
          onOpen={open}
        />
      </Box>
      {rerunError ? <Alert severity="error">{rerunError}</Alert> : null}
      {selectedId ? (
        <RunDetail
          key={selectedId}
          runId={selectedId}
          onRerun={rerun}
          rerunning={rerunning}
          models={options.models}
        />
      ) : null}
    </Stack>
  );
}

export default DemandPlayground;
